import { ValidityState } from '@qualicloud/contracts';
import {
  daysUntilExpiry,
  EXPIRING_SOON_DASHBOARD_DAYS,
  EXPIRING_SOON_LIST_DAYS,
  resolveValidityState,
  validitySemaphore,
  type ValiditySemaphore,
} from '@qualicloud/domain';

/**
 * Apresentação do semáforo de validade.
 *
 * A DECISÃO (qual estado, qual cor) vem inteira de `@qualicloud/domain`:
 * `resolveValidityState` e `validitySemaphore`. Este módulo NÃO reimplementa
 * nenhum limiar — se reimplementasse, a tela poderia divergir do job noturno
 * que materializa `EXPIRED`, e o usuário veria "válido" num documento que o
 * servidor já considera vencido. Aqui só há tradução para pixel e para texto.
 *
 * ACESSIBILIDADE (WCAG 1.4.1 — "uso de cor"):
 * o legado transmitia a situação SÓ pela cor de fundo da célula
 * (`ged_list.twig:220-228`: #ffcccc / #fff5cc / #ccffcc / #e6e6e6). Isso é
 * inacessível para daltonismo, para monocromia e para impressão em preto e
 * branco — e a Lista Mestra é impressa em auditoria. Cada estado ganha aqui,
 * obrigatoriamente, TRÊS sinais independentes:
 *   1. cor  (identidade cromática do legado preservada nos tokens);
 *   2. ícone de FORMA distinta (octógono / triângulo / círculo / traço);
 *   3. TEXTO ("Vencido", "Vencendo", "Válido", "Sem validade").
 */

export interface ValidityPresentation {
  readonly state: ValidityState;
  readonly semaphore: ValiditySemaphore;
  /** Rótulo curto exibido no chip. */
  readonly label: string;
  /** Frase completa para leitor de tela e para `title`. */
  readonly srText: string;
  /** Classes Tailwind de fundo + texto, já com contraste AA garantido. */
  readonly className: string;
  readonly iconName: 'expired' | 'expiring' | 'valid' | 'none';
  readonly daysRemaining: number | null;
}

const SEMAPHORE_CLASS: Record<ValiditySemaphore, string> = {
  red: 'bg-semaphore-red-bg text-semaphore-red-fg',
  amber: 'bg-semaphore-amber-bg text-semaphore-amber-fg',
  green: 'bg-semaphore-green-bg text-semaphore-green-fg',
  grey: 'bg-semaphore-grey-bg text-semaphore-grey-fg',
};

const STATE_LABEL: Record<ValidityState, string> = {
  [ValidityState.EXPIRED]: 'Vencido',
  [ValidityState.EXPIRING_SOON]: 'Vencendo',
  [ValidityState.VALID]: 'Válido',
  [ValidityState.NOT_APPLICABLE]: 'Sem validade',
};

const STATE_ICON: Record<ValidityState, ValidityPresentation['iconName']> = {
  [ValidityState.EXPIRED]: 'expired',
  [ValidityState.EXPIRING_SOON]: 'expiring',
  [ValidityState.VALID]: 'valid',
  [ValidityState.NOT_APPLICABLE]: 'none',
};

/**
 * Monta a apresentação a partir do estado JÁ RESOLVIDO pelo servidor.
 *
 * Este é o caminho normal: a Lista Mestra recebe `validityState`, `semaphore` e
 * `daysUntilExpiry` prontos do CORE, que os calcula com a mesma função de
 * domínio. Recalcular no cliente introduziria divergência por relógio
 * dessincronizado.
 */
export function presentValidity(params: {
  readonly state: ValidityState;
  readonly daysUntilExpiry: number | null;
  readonly validUntil: string | null;
}): ValidityPresentation {
  const semaphore = validitySemaphore(params.state);
  const label = STATE_LABEL[params.state];

  return {
    state: params.state,
    semaphore,
    label,
    srText: buildSrText(params.state, params.daysUntilExpiry, params.validUntil),
    className: SEMAPHORE_CLASS[semaphore],
    iconName: STATE_ICON[params.state],
    daysRemaining: params.daysUntilExpiry,
  };
}

/**
 * Calcula localmente a partir da data de vencimento.
 *
 * Só para PRÉ-VISUALIZAÇÃO no formulário, onde ainda não existe registro no
 * servidor. Delega a `resolveValidityState`/`daysUntilExpiry` do domínio.
 */
export function previewValidity(params: {
  readonly validUntil: Date | null;
  readonly validityMonths: number;
  readonly now?: Date;
  readonly warningWindowDays?: number;
}): ValidityPresentation {
  const now = params.now ?? new Date();
  const state = resolveValidityState({
    validUntil: params.validUntil,
    validityMonths: params.validityMonths,
    now,
    warningWindowDays: params.warningWindowDays ?? EXPIRING_SOON_LIST_DAYS,
  });
  const days = daysUntilExpiry(params.validUntil, now);

  return presentValidity({
    state,
    daysUntilExpiry: days,
    validUntil: params.validUntil ? toIsoDate(params.validUntil) : null,
  });
}

function buildSrText(
  state: ValidityState,
  days: number | null,
  validUntil: string | null,
): string {
  const formattedDate = validUntil ? formatBrDate(validUntil) : null;

  switch (state) {
    case ValidityState.EXPIRED: {
      const overdue = days === null ? null : Math.abs(days);
      const suffix = formattedDate ? ` (venceu em ${formattedDate})` : '';
      if (overdue === null) return `Situação de validade: vencido${suffix}.`;
      if (overdue === 0) return `Situação de validade: vencido hoje${suffix}.`;
      return `Situação de validade: vencido há ${overdue} ${overdue === 1 ? 'dia' : 'dias'}${suffix}.`;
    }
    case ValidityState.EXPIRING_SOON: {
      const suffix = formattedDate ? ` (vence em ${formattedDate})` : '';
      if (days === null) return `Situação de validade: vencendo${suffix}.`;
      if (days === 0) return `Situação de validade: vence hoje${suffix}.`;
      return `Situação de validade: vence em ${days} ${days === 1 ? 'dia' : 'dias'}${suffix}.`;
    }
    case ValidityState.VALID: {
      const suffix = formattedDate ? `, válido até ${formattedDate}` : '';
      if (days === null) return `Situação de validade: válido${suffix}.`;
      return `Situação de validade: válido, restam ${days} ${days === 1 ? 'dia' : 'dias'}${suffix}.`;
    }
    case ValidityState.NOT_APPLICABLE:
    default:
      return 'Situação de validade: documento sem prazo de validade.';
  }
}

function formatBrDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

function toIsoDate(date: Date): string {
  const year = String(date.getUTCFullYear()).padStart(4, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export { EXPIRING_SOON_DASHBOARD_DAYS, EXPIRING_SOON_LIST_DAYS };
